import React, { useState, useEffect } from 'react';
import { X, Sparkles, FileText, Table, Image as ImageIcon, Loader2, AlertCircle } from 'lucide-react';
import { useClerk, useOrganization } from '@clerk/clerk-react';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import './SaveReportModal.css';

function SaveReportModal({ isOpen, onClose, onSave, messageToConvert, messages, hasActiveJob, activeJobName, selectedWorkspace }) {
  const [reportName, setReportName] = useState('');
  const [suggestedName, setSuggestedName] = useState('');
  const [isGeneratingName, setIsGeneratingName] = useState(false);
  const [complexity, setComplexity] = useState('balanced');
  const [outputFormats, setOutputFormats] = useState({
    csv: true,  // CSV is always required
    html: false,
    visuals: false
  });
  const [customInstructions, setCustomInstructions] = useState('');
  const [verifiedActiveJob, setVerifiedActiveJob] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const clerk = useClerk();
  const { organization } = useOrganization();

  // Generate suggested name when modal opens
  useEffect(() => {
    if (isOpen && !suggestedName && messages && messages.length > 0) {
      generateSuggestedName();
    }
  }, [isOpen, messages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Verify active job status with backend when modal opens
  useEffect(() => {
    if (!isOpen || !selectedWorkspace?.workspace_id || !clerk?.session || !organization) {
      return;
    }

    const verifyActiveJob = async () => {
      setIsVerifying(true);
      try {
        const sessionToken = await clerk.session.getToken();
        const orgSlug = getOrgSlug(organization);
        const apiBaseUrl = getApiBaseUrl(orgSlug);
        const queryParams = new URLSearchParams({
          workspace_id: selectedWorkspace.workspace_id
        });

        const response = await fetch(
          `${apiBaseUrl}/saved-reports/active-job?${queryParams}`,
          {
            headers: {
              'Authorization': `Bearer ${sessionToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          setVerifiedActiveJob(data.active_job);
        }
      } catch (error) {
        console.error('Error verifying active job:', error);
        // On error, fall back to prop value
        setVerifiedActiveJob(null);
      } finally {
        setIsVerifying(false);
      }
    };

    verifyActiveJob();
  }, [isOpen, selectedWorkspace?.workspace_id, clerk?.session, organization]);

  // Clear form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setReportName('');
      setSuggestedName('');
      setComplexity('balanced');
      setOutputFormats({ csv: true, html: false, visuals: false });  // CSV always required
      setCustomInstructions('');
      setVerifiedActiveJob(null);
    }
  }, [isOpen]);

  const generateSuggestedName = () => {
    setIsGeneratingName(true);

    // Simple name generation based on message content
    // In a real implementation, this could call an AI endpoint
    setTimeout(() => {
      let suggested = '';

      if (messageToConvert) {
        // Generate name from specific message
        const content = messageToConvert.content || '';
        suggested = extractNameFromContent(content);
      } else if (messages && messages.length > 0) {
        // Generate name from conversation
        const recentMessages = messages.slice(-5);
        const combinedContent = recentMessages
          .filter(m => m.type === 'user' || m.type === 'assistant')
          .map(m => m.content)
          .join(' ');
        suggested = extractNameFromContent(combinedContent);
      }

      setSuggestedName(suggested || 'Report');
      setReportName(suggested || '');
      setIsGeneratingName(false);
    }, 800);
  };

  const extractNameFromContent = (content) => {
    // Extract meaningful keywords from content
    const lowerContent = content.toLowerCase();

    // Common report keywords
    const reportKeywords = [
      'sales', 'revenue', 'customer', 'user', 'product', 'order',
      'inventory', 'transaction', 'analytics', 'performance', 'metrics',
      'summary', 'analysis', 'report', 'dashboard', 'overview'
    ];

    // Find first matching keyword
    for (const keyword of reportKeywords) {
      if (lowerContent.includes(keyword)) {
        return `${keyword.charAt(0).toUpperCase() + keyword.slice(1)} Report`;
      }
    }

    // Check for data-related terms
    if (lowerContent.includes('table') || lowerContent.includes('data')) {
      return 'Data Analysis Report';
    }

    if (lowerContent.includes('chart') || lowerContent.includes('graph')) {
      return 'Visual Analytics Report';
    }

    // Default
    const timestamp = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `Report ${timestamp}`;
  };

  const toggleFormat = (format) => {
    setOutputFormats(prev => ({
      ...prev,
      [format]: !prev[format]
    }));
  };

  const handleSave = () => {
    if (!reportName.trim()) {
      return;
    }

    onSave({
      reportName: reportName.trim(),
      complexity,
      outputFormats,
      customInstructions: customInstructions.trim(),
      messageToConvert
    });
  };

  if (!isOpen) {
    return null;
  }

  const complexityOptions = [
    {
      value: 'simple',
      label: 'Simple',
      description: 'Basic data extraction with minimal formatting'
    },
    {
      value: 'balanced',
      label: 'Balanced',
      description: 'Structured report with key insights and formatting'
    },
    {
      value: 'comprehensive',
      label: 'Comprehensive',
      description: 'Detailed analysis with visualizations and commentary'
    }
  ];

  return (
    <div className="save-modal-overlay" onClick={onClose}>
      <div className="save-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="save-modal-header">
          <h2>{messageToConvert ? 'Convert Output to Report' : 'Save as Report'}</h2>
          <button className="save-modal-close" onClick={onClose} title="Close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="save-modal-body">
          {/* Active Job Warning - use backend-verified status */}
          {verifiedActiveJob && (
            <div className="active-job-warning">
              <AlertCircle size={18} />
              <div className="warning-content">
                <strong>Report Generation in Progress</strong>
                <p>"{verifiedActiveJob.report_name}" is currently being generated. Please wait for it to complete or cancel it before saving a new report.</p>
              </div>
            </div>
          )}
          {/* Report Name */}
          <div className="save-modal-section">
            <label className="save-modal-label">
              Report Name
              {isGeneratingName && <Loader2 size={12} className="name-loader" />}
            </label>
            <div className="name-input-group">
              <input
                type="text"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="Enter report name..."
                className="save-modal-input"
                autoFocus
              />
              {suggestedName && reportName !== suggestedName && (
                <button
                  className="use-suggestion-btn"
                  onClick={() => setReportName(suggestedName)}
                  title="Use suggested name"
                >
                  <Sparkles size={14} />
                  Use: "{suggestedName}"
                </button>
              )}
            </div>
          </div>

          {/* Complexity Level */}
          <div className="save-modal-section">
            <label className="save-modal-label">Complexity Level</label>
            <div className="complexity-options">
              {complexityOptions.map((option) => (
                <button
                  key={option.value}
                  className={`complexity-option ${complexity === option.value ? 'selected' : ''}`}
                  onClick={() => setComplexity(option.value)}
                >
                  <div className="complexity-label">{option.label}</div>
                  <div className="complexity-description">{option.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Output Formats */}
          <div className="save-modal-section">
            <label className="save-modal-label">Output Formats</label>
            <div className="format-options">
              <button
                className={`format-option selected disabled`}
                disabled
                title="CSV is always generated"
              >
                <Table size={18} />
                <span>CSV (Required)</span>
              </button>
              <button
                className={`format-option ${outputFormats.html ? 'selected' : ''}`}
                onClick={() => toggleFormat('html')}
              >
                <FileText size={18} />
                <span>HTML</span>
              </button>
              <button
                className={`format-option ${outputFormats.visuals ? 'selected' : ''}`}
                onClick={() => toggleFormat('visuals')}
              >
                <ImageIcon size={18} />
                <span>Visuals</span>
              </button>
            </div>
            <p className="format-hint">
              CSV is always generated. Select additional formats as needed.
            </p>
          </div>

          {/* Custom Instructions */}
          <div className="save-modal-section">
            <label className="save-modal-label">Additional Instructions (Optional)</label>
            <textarea
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              placeholder="Add any specific requirements or customizations..."
              className="save-modal-textarea"
              rows="2"
            />
            <p className="instructions-hint">
              e.g. "Include executive summary", "Focus on last 30 days"
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="save-modal-footer">
          <button className="save-modal-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            className="save-modal-save"
            onClick={handleSave}
            disabled={!reportName.trim() || isVerifying || !!verifiedActiveJob}
          >
            {messageToConvert ? 'Convert to Report' : 'Save Report'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SaveReportModal;
